import { z } from "zod";
import type { Comment } from "@/services/api/comments";
import type { ImageClassificationResponse } from "@/services/api/files/fileUpload.types";

export type FileType =
  | "image"
  | "video"
  | "document"
  | "presentation"
  | "spreadsheet";

export type ViewMode = "large-gallery" | "gallery" | "table";

export interface ImageDimensions {
  width: number;
  height: number;
}

export interface Thumbnail {
  url: string;
  width: number | null;
  height: number | null;
}

export interface FileItem {
  id: string;
  name: string;
  type: FileType;
  size: string;
  createdDate: string;
  modifiedDate?: string;
  extension: string;
  contentType: string;
  viewCount: number;
  dataType?: string;
  team?: string;
  year?: number;
  event?: string;
  tags: string[];
  thumbnailUrl?: string;
  thumbnailSmallUrl?: string;
  thumbnailLargeUrl?: string;
  imageDimensions?: ImageDimensions;
  smallThumbnail?: Thumbnail | null;
  largeThumbnail?: Thumbnail | null;
  attributeValues?: Array<{ attributeName: string; value: string }>;
  comments?: Comment[];
  classifications?: ImageClassificationResponse[];
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
}

export interface FileExplorerFilters {
  tags: string[];
  name?: string;
  [key: string]: string | number | string[] | undefined;
}

const baseSearchSchema = z.object({
  tab: z.string().optional(),
  tags: z.string().optional(),
  name: z.string().optional(),
  sortBy: z.string().optional(),
  sortDirection: z.enum(["ASC", "DESC"]).optional(),
  fileId: z.string().optional(),
  "file-view": z.enum(["detail"]).optional(),
  // Company staff search params
  staffPage: z.string().optional(),
  staffPageSize: z.string().optional(),
  staffSortBy: z.string().optional(),
  staffSortDirection: z.enum(["ASC", "DESC"]).optional(),
  staffSearchColumn: z.string().optional(),
  staffSearchTerm: z.string().optional(),
  staffDepartment: z.string().optional(),
  staffCountry: z.string().optional(),
  staffSeniority: z.string().optional(),
  staffMinF1ExperienceYears: z.coerce.string().optional(),
  staffMaxF1ExperienceYears: z.coerce.string().optional(),
  staffMinTotalExperienceYears: z.coerce.string().optional(),
  staffMaxTotalExperienceYears: z.coerce.string().optional(),
  staffMaxFakeProbability: z.coerce.string().optional(),
  // Company sizes search params
  sizesCompanyName: z.string().optional(),
  sizesMinHeadcount: z.string().optional(),
  sizesMaxHeadcount: z.string().optional(),
  sizesDepartments: z.string().optional(),
  sizesSortBy: z.string().optional(),
  sizesSortDirection: z.enum(["ASC", "DESC"]).optional(),
  // Job postings search params
  jpPage: z.string().optional(),
  jpPageSize: z.string().optional(),
  jpSortBy: z.string().optional(),
  jpSortDirection: z.string().optional(),
  jpSourceName: z.string().optional(),
  jpLocation: z.string().optional(),
  jpCompany: z.string().optional(),
  jpIsActive: z.string().optional(),
  jpSearchTerm: z.string().optional(),
});

export const fileExplorerSearchSchema = baseSearchSchema.catchall(
  z.string().optional()
);

export type FileExplorerSearchParams = {
  tab?: string;
  tags?: string;
  name?: string;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  fileId?: string;
  "file-view"?: "detail";
  [key: string]: string | number | undefined;
};

export type FileExplorerSearchParamsInput = {
  tab?: string;
  tags?: string;
  name?: string;
  page?: string | number;
  itemsPerPage?: string | number;
  sortBy?: string;
  sortDirection?: "ASC" | "DESC";
  [key: string]: string | number | undefined;
};

export interface FileWithUppyId extends File {
  uppyId: string;
}

export interface FileActionButton {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}
